// @flow
import React, { useRef, useEffect } from 'react'
import styled from 'styled-components'
import { Stage, Layer, Text } from 'react-konva'
import Image from './Image'

import Sample from './Sample'
import Slot from './Slot'
import MeasurementLayer from './MeasurementLayer'

import { colors } from '../styles'

import type { DesignBoardItem, Slot as SlotType } from '../types'
import Divider from './Divider'
import { SAVED_DESIGN_ID_KEY } from '../store'
import { EXPORT_PDF_ACTIVE } from './Editor'

type Props = {
  boardIndex: number,
  dimensions: Object,
  title: string,
  subtitle: string,
  subtitle2?: string,
  divider: Object,
  logo: Object,
  disclaimer: string,
  items: Array<DesignBoardItem>,
  slots: Array<SlotType>,
  isItemSelected: Function,
  onChangeItem: Function,
  onSelectItem: Function,
  onChangeDivider: Function,
  onDeselect: Function,
  editable: boolean,
  landingSpot: Object,
  fontLoaded: boolean,
  isCollection: boolean,
}

const BACKGROUND_URLS = [
  'http://design-board.localdev/wp-content/uploads/Sample%20Closet%20Layout%20Template%20-%2006.08.2021%20-%20Kitchen.jpg',
  'http://design-board.localdev/wp-content/uploads/Sample%20Closet%20Layout%20Template%20-%2006.08.2021%20-%20Owner%20Bath.jpg',
  'http://design-board.localdev/wp-content/uploads/Sample%20Closet%20Layout%20Template%20-%2006.08.2021%20-%20Secondary%20Baths.jpg',
]

export const MEASURE_BOX_ACTIVE = false
const LOGO_HEIGHT = 120
const DISCLAIMER_WIDTH = 200

function LayoutSingleBoard (props: Props) {
  // console.log('LayoutSingleBoard', props)

  const {
    boardIndex,
    editable,
    title,
    subtitle,
    subtitle2,
    divider,
    logo,
    disclaimer,
    dimensions,
    isItemSelected,
    onSelectItem,
    onChangeItem,
    isSlotSelected,
    onSelectSlot,
    items,
    slots,
    onChangeDivider,
    fontLoaded,
    isCollection,
  } = props
  const stageRef = useRef(null)

  useEffect(() => {
    if (EXPORT_PDF_ACTIVE) {
      console.log('saving board image....', boardIndex)
      const designId = sessionStorage.getItem(SAVED_DESIGN_ID_KEY)
      const boardKey = `board_image_${designId}_${boardIndex}`
      const value = (items.length > 0) ? stageRef.current.toDataURL({ pixelRatio: 2 }) : null
      sessionStorage.setItem(boardKey, value)
    }
  }, [ logo, boardIndex, title, subtitle, subtitle2, items ])

  const background_url = BACKGROUND_URLS[ boardIndex ]
  const landingSpot = MEASURE_BOX_ACTIVE ? null : props.landingSpot
  const onDeselect = MEASURE_BOX_ACTIVE ? () => {} : props.onDeselect
  const usedSlotIds = items.reduce((acc, item) => {
    if (item.slotId && !acc.includes(item.slotId)) {
      acc.push(item.slotId)
    }
    return acc
  }, [])
  const showSlots = slots.filter(s => !usedSlotIds.includes(s.id))

  const textAttrs = {
    fontSize: 28,
    fontFamily: 'HelveticaNeue_Lt',
    strokeWidth: 1,
    fill: colors.midGray,
    stroke: colors.midGray,
  }

  const disclaimerAttrs = {
    fontSize: 12,
    fontFamily: 'HelveticaNeue_Lt',
    fill: colors.darkOrange,
    align: 'right',
    x: (dimensions.width / dimensions.scaleX) - DISCLAIMER_WIDTH - 20,
    width: DISCLAIMER_WIDTH,
    y: 10,
  }
  // console.log('disclaimer', dimensions, disclaimerAttrs)

  const logoAttrs = {
    x: 10,
    y: (dimensions.height / dimensions.scaleY) - LOGO_HEIGHT - 10,
    width: logo ? LOGO_HEIGHT * logo.attributes.scaleX : 0,
    height: LOGO_HEIGHT,
    draggable: false,
  }
  // console.log('LayoutSingleBoard', dimensions, logoAttrs, disclaimerAttrs)
  return (
    <LayerContainer style={{ ...dimensions }} data-page={boardIndex + 1}>
      <DivLayer>
        <Stage x={0} y={0} {...dimensions} onMouseDown={onDeselect} onTouchStart={onDeselect} ref={stageRef}>
          {editable && (
            <Layer>
              {MEASURE_BOX_ACTIVE && <Image url={background_url} scaleX={1.38} scaleY={1.40} opacity={0.5} x={0} y={0} />}

              {showSlots.map((slot, index) => {
                  return (
                    <Slot
                      key={`slot-${index}`}
                      slot={slot}
                      isSelected={isSlotSelected(slot)}
                      onSelect={() => onSelectSlot(slot)}
                    />
                  )
                }
              )}
              {(editable && landingSpot) && (
                <Slot slot={{ image: { x: landingSpot.x - 40, y: landingSpot.y - 30, width: 50, height: 50 } }} isSelected={true} />
              )}
            </Layer>
          )}
          {MEASURE_BOX_ACTIVE && editable && (<MeasurementLayer dimensions={dimensions} />)}
          <Layer>
            {(subtitle2 && divider) && <Divider attributes={{ ...divider }} onChange={onChangeDivider} draggable={editable} editable={editable} />}

            {items.map((item: DesignBoardItem, index) => {
              // console.log('db.item', (item.id === activeItemId), item)
              return <Sample key={`${item.id}-${index}`}
                             item={item}
                             editable={editable}
                             scaleX={dimensions.scaleX}
                             scaleY={dimensions.scaleY}
                             isSelected={isItemSelected(item)}
                             onSelect={() => onSelectItem(item)}
                             onChange={(attrs, itemChanges) => onChangeItem({ ...item, ...itemChanges }, attrs)} />
            })}
          </Layer>

          <Layer listening={!fontLoaded}>
            {!!title && <Text text={title} {...textAttrs} x={10} y={20} />}
            {!!subtitle && <Text text={subtitle} {...textAttrs} x={10} y={64} />}

            {!!subtitle2 && (
              <>
                {!!title && <Text text={title} {...textAttrs} x={divider.x + 13} y={20} />}
                {!!subtitle2 && <Text text={subtitle2} {...textAttrs} x={divider.x + 13} y={64} />}
              </>
            )}

          </Layer>
          <Layer listening={false}>
            <Text {...disclaimerAttrs} text={disclaimer} />
            <Image {...logoAttrs} url={logo?.url} />
          </Layer>
        </Stage>
      </DivLayer>

      <DivLayer style={{ pointerEvents: 'none' }}>
        <StaticContentContainer>
          {/* for pre-loading */}
          <HiddenImageContainer>
            {items.map((item: DesignBoardItem, index) => {
              if (item.image) {
                return <img alt={item.image.label} key={`${item.id}-${index}`} src={item.image.imageUrl} id={`img-${item.id}`} />
              }
              return null
            })}
          </HiddenImageContainer>
        </StaticContentContainer>
      </DivLayer>

    </LayerContainer>
  )
}

LayoutSingleBoard.defaultProps = {
  isItemSelected: () => {},
  onSelectItem: () => {},
  onChangeItem: () => {},
}

export default LayoutSingleBoard

const LayerContainer = styled.div`
  width: 100%;
  height: 100%;
  background: #fff;
  position: relative;
  padding: 10px;

  .canvas-container, .upper-canvas, #canvas {
    width: 100% !important;
    height: 100% !important;
  }
  
  canvas {
    z-index: 1;
  }
  @media print {
    page-break-after: always;
    width: 11in !important;
    height: 8.5in !important;
    
    canvas {
      width: 11in !important;
      height: 8.5in !important;
    }
  }
`

const DivLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`

const StaticContentContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 100;
`

const HiddenImageContainer = styled.div`
  display: none;
  position: absolute;
  bottom: 0;
  right: 0;

  img { max-width: 32px; }
`
